import type { ReactNode } from "react";
import {
  CheckmarkCircleFilled,
  DiamondDismiss12Filled,
  ArrowSync12Filled,
  ArrowClockwiseDashesFilled,
} from "@fluentui/react-icons";
import type { RequestStatus } from "@/types/requestTypes";
import styles from "./RequestStatusBadge.module.scss";

interface IRequestStatusBadgeProps {
  status: RequestStatus;
  detail?: string;
}

interface IStatusConfig {
  label: string;
  icon: ReactNode;
  iconClass: string;
}

function getStatusConfig(status: RequestStatus, detail?: string): IStatusConfig {
  switch (status) {
    case "SelfApproved":
      return {
        label: "Self-approved",
        icon: <CheckmarkCircleFilled />,
        iconClass: styles.iconApproved,
      };
    case "Approved":
      return {
        label: detail ? `Approved (${detail})` : "Approved",
        icon: <CheckmarkCircleFilled />,
        iconClass: styles.iconApproved,
      };
    case "Rejected":
      return {
        label: detail ? `Rejected (${detail})` : "Rejected",
        icon: <DiamondDismiss12Filled />,
        iconClass: styles.iconRejected,
      };
    case "Generating":
      return {
        label: "Generating",
        icon: <ArrowSync12Filled />,
        iconClass: styles.iconGenerating,
      };
    case "PendingManagerApproval":
      return {
        label: "Pending manager approval",
        icon: <ArrowClockwiseDashesFilled />,
        iconClass: styles.iconPending,
      };
    default:
      throw new Error(`Unhandled status: ${String(status)}`);
  }
}

export function RequestStatusBadge({ status, detail }: IRequestStatusBadgeProps): ReactNode {
  const config = getStatusConfig(status, detail);

  return (
    <span className={styles.badge} aria-label={`Status: ${config.label}`}>
      <span className={`${styles.icon} ${config.iconClass}`} aria-hidden="true">
        {config.icon}
      </span>
      <span>{config.label}</span>
    </span>
  );
}
